import type { KnowledgeParticle } from '../../types/domain';
import { knowledgeService } from '../../services/knowledgeService';
import { seedParticleSourceImageCache } from './particleSourceImageCache';

const particleCache = new Map<string, KnowledgeParticle>();
const inflight = new Map<string, Promise<KnowledgeParticle>>();
const batchInflight = new Map<string, Promise<Record<string, KnowledgeParticle | null>>>();

/** Coalesce bursty single-id card mounts into one batch POST. */
const COALESCE_MS = 32;
let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
const coalesceWaiters = new Map<string, Array<{
  resolve: (particle: KnowledgeParticle) => void;
  reject: (error: unknown) => void;
}>>();

export function getWorkbenchParticleCache(particleId: string): KnowledgeParticle | null {
  return particleCache.get(particleId) ?? null;
}

export function setWorkbenchParticleCache(particle: KnowledgeParticle): void {
  if (!particle?.id) return;
  particleCache.set(particle.id, particle);
}

export function invalidateWorkbenchParticleCache(particleId?: string): void {
  if (particleId) {
    particleCache.delete(particleId);
    inflight.delete(particleId);
    return;
  }
  particleCache.clear();
  inflight.clear();
  batchInflight.clear();
  coalesceWaiters.clear();
  if (coalesceTimer) {
    clearTimeout(coalesceTimer);
    coalesceTimer = null;
  }
}

export async function refreshWorkbenchParticle(particleId: string): Promise<KnowledgeParticle> {
  invalidateWorkbenchParticleCache(particleId);
  return fetchWorkbenchParticle(particleId);
}

function rememberParticle(particle: KnowledgeParticle): KnowledgeParticle {
  particleCache.set(particle.id, particle);
  seedParticleSourceImageCache(particle.id, particle.metadata ?? null);
  return particle;
}

async function runCoalescedBatch() {
  const ids = [...coalesceWaiters.keys()];
  const waitersById = new Map(coalesceWaiters);
  coalesceWaiters.clear();
  coalesceTimer = null;
  if (!ids.length) return;

  try {
    const byId = await fetchWorkbenchParticles(ids);
    for (const id of ids) {
      const waiters = waitersById.get(id) || [];
      const particle = byId[id];
      if (particle) {
        for (const waiter of waiters) waiter.resolve(particle);
      } else {
        const error = new Error('知识颗粒不存在');
        for (const waiter of waiters) waiter.reject(error);
      }
    }
  } catch (error) {
    for (const waiters of waitersById.values()) {
      for (const waiter of waiters) waiter.reject(error);
    }
  }
}

function enqueueCoalescedFetch(particleId: string): Promise<KnowledgeParticle> {
  return new Promise((resolve, reject) => {
    const queue = coalesceWaiters.get(particleId) || [];
    queue.push({ resolve, reject });
    coalesceWaiters.set(particleId, queue);
    if (!coalesceTimer) {
      coalesceTimer = setTimeout(() => {
        void runCoalescedBatch();
      }, COALESCE_MS);
    }
  });
}

export async function fetchWorkbenchParticle(particleId: string): Promise<KnowledgeParticle> {
  const id = String(particleId || '').trim();
  if (!id) {
    throw new Error('知识颗粒ID无效');
  }
  const cached = particleCache.get(id);
  if (cached) return cached;

  const pending = inflight.get(id);
  if (pending) return pending;

  const promise = enqueueCoalescedFetch(id)
    .then((particle) => rememberParticle(particle))
    .finally(() => {
      inflight.delete(id);
    });

  inflight.set(id, promise);
  return promise;
}

/** One batch POST for missing ids; hits process cache first. */
export async function fetchWorkbenchParticles(
  particleIds: string[],
): Promise<Record<string, KnowledgeParticle | null>> {
  const ids = [...new Set(particleIds.map((id) => String(id || '').trim()).filter(Boolean))];
  const result: Record<string, KnowledgeParticle | null> = {};
  const missing: string[] = [];

  for (const id of ids) {
    const cached = particleCache.get(id);
    if (cached) {
      result[id] = cached;
    } else {
      missing.push(id);
    }
  }

  if (!missing.length) {
    return result;
  }

  const batchKey = missing.slice().sort().join(',');
  const pendingBatch = batchInflight.get(batchKey);
  if (pendingBatch) {
    const batched = await pendingBatch;
    for (const id of missing) {
      result[id] = batched[id] ?? null;
    }
    return result;
  }

  const promise = knowledgeService.getKnowledgeParticlesByIds(missing)
    .then((particles) => {
      const byId: Record<string, KnowledgeParticle | null> = {};
      for (const id of missing) {
        byId[id] = null;
      }
      for (const particle of particles || []) {
        if (!particle?.id) continue;
        byId[particle.id] = rememberParticle(particle);
      }
      return byId;
    })
    .catch(async () => {
      const byId: Record<string, KnowledgeParticle | null> = {};
      await Promise.all(missing.map(async (id) => {
        try {
          const cached = particleCache.get(id);
          if (cached) {
            byId[id] = cached;
            return;
          }
          const particle = await knowledgeService.getKnowledgeParticle(id);
          byId[id] = rememberParticle(particle);
        } catch {
          byId[id] = null;
        }
      }));
      return byId;
    })
    .finally(() => {
      batchInflight.delete(batchKey);
    });

  batchInflight.set(batchKey, promise);
  const batched = await promise;
  for (const id of missing) {
    result[id] = batched[id] ?? null;
  }
  return result;
}
